// Cloud Signals — browser data layer.
//
// Mirrors strategyTesterDataFeeds.js: same API base resolution, same fetch wrapper, same error
// shape (message plus a machine-readable `code`, so callers can branch on VIX_PREMIUM_REQUIRED
// rather than string-matching).

const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/i;
const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && LOCAL_HOST_PATTERN.test(window.location.hostname)) {
    return 'http://localhost:5000';
  }
  return '';
})();

function authToken() {
  return typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;
}

async function requestSignals(path, options = {}) {
  const { query = {}, method = 'GET', body } = options;
  const url = new URL(
    `${API_BASE_URL}${path}`,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  );
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  const token = authToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(url.toString(), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || 'Signal request failed.');
    error.status = response.status;
    if (payload.code) error.code = payload.code;
    throw error;
  }
  return payload;
}

// ── Deployments ───────────────────────────────────────────────────────────────

export function listDeployments() {
  return requestSignals('/api/signals/deployments');
}

export function getDeployment(id) {
  return requestSignals(`/api/signals/deployments/${id}`);
}

export function deploySignal({ strategyId, name, symbol, timeframe, virtualDeposit, visibility }) {
  return requestSignals('/api/signals/deployments', {
    method: 'POST',
    body: { strategyId, name, symbol, timeframe, virtualDeposit, visibility },
  });
}

// Deploy an imported MQL5 EA as a live signal. The bundle is analysed server-side and only the
// compiled program is kept — the .mq5/.mqh files are never stored, which is the whole basis on
// which people upload a paid EA.
export function deployEaSignal({
  files, entry, name, symbol, timeframe, virtualDeposit, visibility, leverage, inputs,
}) {
  return requestSignals('/api/signals/deployments/ea', {
    method: 'POST',
    body: { files, entry, name, symbol, timeframe, virtualDeposit, visibility, leverage, inputs },
  });
}

export function updateDeployment(id, patch) {
  return requestSignals(`/api/signals/deployments/${id}`, { method: 'PATCH', body: patch });
}

export function deleteDeployment(id) {
  return requestSignals(`/api/signals/deployments/${id}`, { method: 'DELETE' });
}

export function listDeploymentTrades(id, { limit = 100, offset = 0 } = {}) {
  return requestSignals(`/api/signals/deployments/${id}/trades`, { query: { limit, offset } });
}

export function listPublicSignals() {
  return requestSignals('/api/signals/public');
}

// ── Notifications ─────────────────────────────────────────────────────────────

export function getNotificationPrefs(deploymentId = 0) {
  return requestSignals('/api/signals/notification-prefs', { query: { deploymentId } });
}

export function saveNotificationPrefs({ deploymentId = 0, channels, notifyOn, muted, discordWebhookUrl }) {
  return requestSignals('/api/signals/notification-prefs', {
    method: 'PUT',
    body: { deploymentId, channels, notifyOn, muted, discordWebhookUrl },
  });
}

export function sendTestNotification(channel) {
  return requestSignals('/api/signals/notifications/test', { method: 'POST', body: { channel } });
}

export function listNotifications({ since = 0, limit = 50 } = {}) {
  return requestSignals('/api/signals/notifications', { query: { since, limit } });
}

// ── Telegram ──────────────────────────────────────────────────────────────────

export function getTelegramStatus() {
  return requestSignals('/api/signals/telegram/status');
}

export function startTelegramLink() {
  return requestSignals('/api/signals/telegram/link', { method: 'POST' });
}

export function unlinkTelegram() {
  return requestSignals('/api/signals/telegram/link', { method: 'DELETE' });
}

// ── Presentation helpers ──────────────────────────────────────────────────────

export const CHANNEL_LABELS = {
  telegram: 'Telegram',
  email: 'Email',
  inapp: 'In-app',
  discord: 'Discord',
};

export const EVENT_LABELS = {
  trade_open: 'Trade opened',
  trade_close: 'Trade closed',
  position_update: 'Stop / target moved',
  drift_alert: 'Drifting from backtest',
  milestone: 'Milestone reached',
};

export function formatSignalMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  const sign = number > 0 ? '+' : '';
  return `${sign}${number.toFixed(2)}`;
}

export function formatBarTime(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '—';
  return new Date(number).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Summary stats computed from the trade ledger. Derived in the browser rather than fetched so
// the cards stay responsive while a deployment is being filtered or paged.
export function summarizeSignalTrades(trades) {
  const closed = (trades || []).filter((trade) => trade.status === 'closed');
  const wins = closed.filter((trade) => Number(trade.pnl) > 0);
  const losses = closed.filter((trade) => Number(trade.pnl) < 0);
  const grossWin = wins.reduce((sum, trade) => sum + Number(trade.pnl || 0), 0);
  const grossLoss = Math.abs(losses.reduce((sum, trade) => sum + Number(trade.pnl || 0), 0));
  const rValues = closed
    .map((trade) => Number(trade.rMultiple))
    .filter((value) => Number.isFinite(value));
  return {
    total: closed.length,
    open: (trades || []).filter((trade) => trade.status === 'open').length,
    wins: wins.length,
    losses: losses.length,
    winRate: closed.length ? (wins.length / closed.length) * 100 : 0,
    netProfit: closed.reduce((sum, trade) => sum + Number(trade.pnl || 0), 0),
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : (grossWin > 0 ? Infinity : 0),
    avgR: rValues.length ? rValues.reduce((sum, value) => sum + value, 0) / rValues.length : null,
  };
}

// Peak-to-trough drawdown of the equity series, as a percentage.
export function maxDrawdownPct(equity) {
  let peak = -Infinity;
  let worst = 0;
  for (const point of equity || []) {
    const value = Number(point.equity);
    if (!Number.isFinite(value)) continue;
    if (value > peak) peak = value;
    if (peak > 0) worst = Math.max(worst, ((peak - value) / peak) * 100);
  }
  return worst;
}

// ── Marketplace ───────────────────────────────────────────────────────────────

export function listPublicMarketplace({ symbol, timeframe, sort, ranked } = {}) {
  return requestSignals('/api/signals/public', {
    query: { symbol, timeframe, sort, ranked: ranked ? 1 : '' },
  });
}

export function getLeaderboard({ sort } = {}) {
  return requestSignals('/api/signals/leaderboard', { query: { sort } });
}

export function getCreatorProfile(userId) {
  return requestSignals(`/api/signals/creators/${userId}`);
}

// ── Paper follow ──────────────────────────────────────────────────────────────

export function followSignal(deploymentId, { deposit, riskMode, riskValue }) {
  return requestSignals(`/api/signals/deployments/${deploymentId}/follow`, {
    method: 'POST',
    body: { deposit, riskMode, riskValue },
  });
}

export function unfollowSignal(deploymentId) {
  return requestSignals(`/api/signals/deployments/${deploymentId}/follow`, { method: 'DELETE' });
}

export function listFollows() {
  return requestSignals('/api/signals/follows');
}

export const RISK_MODE_LABELS = {
  proportional: 'Mirror the creator',
  fixed_lot: 'Fixed lot size',
  percent_risk: 'Risk % per trade',
};

export const RISK_MODE_HINTS = {
  proportional: 'Same trades, scaled to your deposit.',
  fixed_lot: 'Always trade the same volume.',
  percent_risk: 'Risk a set % of your balance on each trade — this one compounds.',
};

// ── Listings, subscriptions and earnings ──────────────────────────────────────

export function listPriceTiers() {
  return requestSignals('/api/signals/tiers');
}

export function createListing(deploymentId, { tierId, headline, description }) {
  return requestSignals(`/api/signals/deployments/${deploymentId}/listing`, {
    method: 'POST',
    body: { tierId, headline, description },
  });
}

export function removeListing(deploymentId) {
  return requestSignals(`/api/signals/deployments/${deploymentId}/listing`, { method: 'DELETE' });
}

export function subscribeToListing(listingId) {
  return requestSignals(`/api/signals/listings/${listingId}/subscribe`, { method: 'POST' });
}

export function cancelSubscription(subscriptionId) {
  return requestSignals(`/api/signals/subscriptions/${subscriptionId}`, { method: 'DELETE' });
}

export function listSubscriptions() {
  return requestSignals('/api/signals/subscriptions');
}

export function getEarnings() {
  return requestSignals('/api/signals/earnings');
}

export function getPayoutAccount() {
  return requestSignals('/api/signals/payout-account');
}

export function savePayoutAccount(payoutEmail) {
  return requestSignals('/api/signals/payout-account', { method: 'PUT', body: { payoutEmail } });
}

export function verifyPayoutAccount(token) {
  return requestSignals('/api/signals/payout-account/verify', { method: 'POST', body: { token } });
}

// ── MT5 copier ────────────────────────────────────────────────────────────────

export function getCopierToken() {
  return requestSignals('/api/signals/copier/token');
}

export function issueCopierToken(label) {
  return requestSignals('/api/signals/copier/token', { method: 'POST', body: { label } });
}

export function getCopierEaMeta() {
  return requestSignals('/api/signals/copier/ea-meta');
}

// Served by the backend, not the static build — see signalCopier.js.
export const COPIER_EA_URL = `${API_BASE_URL}/api/signals/copier/ea`;

export function revokeCopierToken() {
  return requestSignals('/api/signals/copier/token', { method: 'DELETE' });
}

// Live MT5 tracking for the copier account. On by default — the EA is handed its tracking
// token in the copier feed — so these only exist for turning it off and back on again.
export function getCopierTracking() {
  return requestSignals('/api/signals/copier/tracking');
}

export function enableCopierTracking() {
  return requestSignals('/api/signals/copier/tracking', { method: 'POST' });
}

export function disableCopierTracking() {
  return requestSignals('/api/signals/copier/tracking', { method: 'DELETE' });
}

export function getCopierSettings() {
  return requestSignals('/api/signals/copier/settings');
}

export function saveCopierSizing(deploymentId, { sizingMode, sizingValue, copyEnabled }) {
  return requestSignals(`/api/signals/copier/settings/${deploymentId}`, {
    method: 'PUT',
    body: { sizingMode, sizingValue, copyEnabled },
  });
}

// Lot sizing cannot be one global number. This broker alone has 33 different minimum volumes
// across its symbol list (0.01 on EURUSD, 1 on most synthetics, 4 on Volatility 50, 10 on
// Volatility 250 (1s)), so a fixed size is invalid on most of them. Everything here is expressed
// relative to whatever the symbol's own minimum turns out to be in the terminal.
export const SIZING_MODE_LABELS = {
  inherit: 'Use the EA’s own setting',
  min: 'Smallest this symbol allows',
  min_multiple: 'Multiple of the minimum',
  fixed: 'Fixed lot',
  risk_pct: 'Risk % of balance',
  mirror: 'Mirror the creator',
};

export const SIZING_MODE_HINTS = {
  inherit: 'Whatever InpSizingMode is set to in MetaTrader.',
  min: 'Safest across mixed symbols — resolves per symbol, in your terminal.',
  min_multiple: 'e.g. 2 = twice the minimum, whatever that is for each symbol.',
  fixed: 'Skipped on any symbol whose minimum is larger than this.',
  risk_pct: 'Needs the signal to carry a stop loss.',
  mirror: 'Creator’s volume scaled to your balance.',
};

// Modes that are meaningless without a number.
export const SIZING_MODES_NEEDING_VALUE = ['min_multiple', 'fixed', 'risk_pct'];

export const LEDGER_TYPE_LABELS = {
  earning: 'Subscription payment',
  clawback: 'Refund reversed',
  payout: 'Paid out',
};

// ── Axis helpers ──────────────────────────────────────────────────────────────
//
// Shared so every chart on the site labels its axes the same way. An unlabelled curve is a
// shape, not a measurement — the reader cannot tell a 2% drift from a 40% collapse.

/**
 * "Nice" round tick values spanning [min, max] — 1/2/5 × a power of ten.
 *
 * Returns the ROUNDED bounds alongside the ticks, because the curve must be plotted against the
 * same bounds the labels describe or the gridlines will not line up with them.
 */
export function niceScale(min, max, targetCount = 5) {
  const span = max - min;
  if (!(span > 0)) return { ticks: [min], niceMin: min - 1, niceMax: max + 1 };
  const rawStep = span / targetCount;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  // Guarded rather than a bare while: a degenerate step would otherwise spin forever.
  for (let value = niceMin, guard = 0; value <= niceMax + step / 2 && guard < 40; value += step, guard += 1) {
    ticks.push(Number(value.toFixed(6)));
  }
  return { ticks, niceMin, niceMax };
}

// Evenly spaced date labels across a time span, for an x axis.
export function axisTimeTicks(minTime, maxTime, count = 5) {
  const span = Number(maxTime) - Number(minTime);
  if (!(span > 0)) return [];
  const ticks = [];
  for (let i = 0; i < count; i += 1) {
    const ratio = i / (count - 1);
    const time = Number(minTime) + span * ratio;
    ticks.push({
      time,
      ratio,
      label: new Date(time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    });
  }
  return ticks;
}

// ── Period aggregation ────────────────────────────────────────────────────────

// Group closed trades into calendar buckets for the profit-by-period bars. Only LIVE trades
// should ever be passed in — bucketing seed history would advertise replayed results.
export function bucketTradesByPeriod(trades, period = 'month') {
  const buckets = new Map();
  for (const trade of trades || []) {
    if (trade.status !== 'closed') continue;
    const when = new Date(Number(trade.exitTime || trade.entryTime));
    if (Number.isNaN(when.getTime())) continue;

    let key;
    let label;
    if (period === 'day') {
      key = when.toISOString().slice(0, 10);
      label = when.toLocaleDateString([], { day: 'numeric', month: 'short' });
    } else if (period === 'week') {
      // ISO-ish week key: the Monday of that week.
      const monday = new Date(when);
      monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
      key = monday.toISOString().slice(0, 10);
      label = monday.toLocaleDateString([], { day: 'numeric', month: 'short' });
    } else {
      key = when.toISOString().slice(0, 7);
      label = when.toLocaleDateString([], { month: 'short', year: '2-digit' });
    }

    const existing = buckets.get(key) || { key, label, net: 0, trades: 0, wins: 0 };
    existing.net += Number(trade.pnl || 0);
    existing.trades += 1;
    if (Number(trade.pnl) > 0) existing.wins += 1;
    buckets.set(key, existing);
  }
  return [...buckets.values()].sort((a, b) => a.key.localeCompare(b.key));
}

// Nicely spaced date ticks for a time axis, without pulling in a charting library.
export function buildTimeTicks(points, count = 5) {
  const times = (points || []).map((point) => Number(point.barTime ?? point.time)).filter(Number.isFinite);
  if (times.length < 2) return [];
  const min = Math.min(...times);
  const max = Math.max(...times);
  const span = max - min;
  if (!(span > 0)) return [];
  const spanDays = span / 86400000;
  // Below a couple of days a date alone is useless, so include the time.
  const format = spanDays > 60
    ? { month: 'short', year: '2-digit' }
    : spanDays > 2
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', hour: '2-digit' };
  return Array.from({ length: count }, (_, index) => {
    const time = min + ((span * index) / (count - 1));
    return { time, ratio: (time - min) / span, label: new Date(time).toLocaleDateString([], format) };
  });
}
